import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./accValidation.module.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { endOfDay, startOfDay } from "date-fns";
import AccValidationModal from "../../components/AccValidationModal/AccValidationModal";
import axios from "axios";
import Config from "../../service/Config";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
  Popover,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import { useNavigate } from "react-router-dom";
import {
  DataGrid,
  useGridApiRef,
  GridPreferencePanelsValue,
  getGridStringOperators,
  getGridSingleSelectOperators,
  gridPaginatedVisibleSortedGridRowIdsSelector,
  gridFilteredSortedRowEntriesSelector,
} from "@mui/x-data-grid";

/* ------------------------------------------------------------------
 * Typography / colours
 * ------------------------------------------------------------------ */
const SARABUN = "var(--font-sarabun), sans-serif";
const SANSOURCE = "var(--font-source-sans-3), sans-serif";

const HEADER_COLOR = "#666666";
const TEXT_COLOR = "#4C4E64";
const ACCENT_COLOR = "#3246A9";
const MUTED_COLOR = "#8C8C8C";

/* ------------------------------------------------------------------
 * Grid sizing
 * ------------------------------------------------------------------ */
const GRID_ROW_HEIGHT = 74;
const GRID_HEADER_HEIGHT = 56;
const GRID_FOOTER_HEIGHT = 53;
const GRID_MAX_HEIGHT = "96vh";
const GRID_MIN_ROWS = 3;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

const CONTAINS_ONLY = getGridStringOperators().filter(
  (op) => op.value === "contains",
);
const IS_ONLY = getGridSingleSelectOperators().filter(
  (op) => op.value === "is",
);

const parseQuickFilter = (input) =>
  input.split(" ").filter((word) => word !== "");

const EMPTY_FILTER_MODEL = {
  items: [],
  quickFilterValues: [],
  quickFilterExcludeHiddenColumns: false,
};

const DEFAULT_HIDDEN_COLUMNS = {};

// All records are loaded once and paginated / filtered by the grid,
// so the row count always matches what is actually shown.
const FETCH_LIMIT = 100; // records per request while loading
const FETCH_CONCURRENCY = 5; // parallel requests per batch

const isWithinRange = (createdAt, from, to) => {
  if (!createdAt) return false;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return false;
  return (!from || d >= from) && (!to || d <= to);
};

/* ------------------------------------------------------------------
 * Panel styling
 * ------------------------------------------------------------------ */
const PANEL_SX = {
  "& , & *": { fontFamily: SANSOURCE },
  "& .MuiDataGrid-panelWrapper": { fontFamily: SANSOURCE },
  "& .MuiFormControlLabel-label, & .MuiTypography-root": {
    fontFamily: SANSOURCE,
    fontSize: 14,
    fontWeight: 500,
    color: TEXT_COLOR,
    letterSpacing: 0,
    textTransform: "none",
  },
  "& .MuiInputBase-root, & .MuiInputBase-input, & .MuiFormLabel-root, & .MuiSelect-select":
  {
    fontFamily: SANSOURCE,
    fontSize: 14,
    color: TEXT_COLOR,
  },
  "& .MuiInputBase-input::placeholder": {
    fontFamily: SANSOURCE,
    color: MUTED_COLOR,
    opacity: 1,
  },
  "& .MuiDataGrid-panelFooter .MuiButton-root": {
    fontFamily: SANSOURCE,
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    color: ACCENT_COLOR,
  },
  "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate":
  {
    color: ACCENT_COLOR,
  },
  "& .MuiPaper-root, & .MuiDataGrid-paper": {
    borderRadius: "var(--br-xs)",
    overflow: "hidden",
  },
  "& .MuiDataGrid-panelWrapper": {
    borderRadius: "var(--br-xs)",
    overflow: "hidden",
  },
};

const SELECT_MENU_SX = {
  borderRadius: "var(--br-xs)",
  "& .MuiMenuItem-root": {
    fontFamily: SARABUN,
    fontSize: 14,
    fontWeight: 500,
    color: TEXT_COLOR,
    letterSpacing: 0,
    textTransform: "none",
  },
  "& .MuiMenuItem-root.Mui-selected": {
    color: ACCENT_COLOR,
    backgroundColor: "rgba(50, 70, 169, 0.08)",
  },
  "& .MuiMenuItem-root.Mui-selected:hover": {
    backgroundColor: "rgba(50, 70, 169, 0.12)",
  },
};

/* ------------------------------------------------------------------
 * Auth helper
 * ------------------------------------------------------------------ */


/* ------------------------------------------------------------------
 * Row mapper
 * ------------------------------------------------------------------ */
const mapItemToRow = (it, index) => {
  const status = it.status || (it.verified ? "verified" : "unverified");
  return {
    id: it.reference_id || `${it.company_id}-${index}`,
    reference_id: it.reference_id,
    company_id: it.company_id,
    company_name: it.company_name,
    bank_name: it.bank_name,
    ifsc: it.ifsc,
    acc_no: it.acc_no,
    acc_no_last4: it.acc_no_last4,
    beneficiary_name: it.beneficiary_name,
    verified: !!it.verified,
    status,
    created_at: it.created_at,
    __date: it.created_at
      ? new Date(it.created_at).toLocaleDateString()
      : "—",
    __compName: it.company_name || it.company_id || "—",
    __accNo: it.acc_no || "—",
    __beneName: it.beneficiary_name || "—",
    __status: status,
    __raw: it,
  };
};

const ellipsisCell = (value) => (
  <Tooltip title={value || ""}>
    <span
      style={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </span>
  </Tooltip>
);

const AccValidation = ({ title }) => {
  const navigate = useNavigate();
  const API_BASE = Config.kyc_api_endpoint;
  const apiRef = useGridApiRef();
  const columnsButtonRef = useRef(null);

  const [data, setData] = useState([]); // every record from the API
  const [fetching, setFetching] = useState(false);
  const [refresh, setRefresh] = useState(false);

  // modal
  const [accDetailsTab, setAccDetailsTab] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // date filter (applied values filter the rows; drafts live in the popover)
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startDateDraft, setStartDateDraft] = useState(null);
  const [endDateDraft, setEndDateDraft] = useState(null);

  // DataGrid controls
  const [searchText, setSearchText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterModel, setFilterModel] = useState(EMPTY_FILTER_MODEL);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    DEFAULT_HIDDEN_COLUMNS,
  );
  const [panelAnchorEl, setPanelAnchorEl] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  // rows actually visible on the current page (after filters + pagination)
  const [visibleRowCount, setVisibleRowCount] = useState(0);

  const isDateFilterActive = Boolean(startDate || endDate);

  const goToFirstPage = () =>
    setPaginationModel((m) => (m.page === 0 ? m : { ...m, page: 0 }));

  function getAuthHeaders() {
    const tokens = localStorage.getItem("authToken");
    if (!tokens) {

      localStorage.removeItem("username");
      localStorage.removeItem("email");
      localStorage.removeItem("authToken");
      navigate("/");
      return null;
    }
    try {
      const parsed = JSON.parse(tokens);
      if (!parsed?.IdToken || !parsed?.AccessToken) return null;
      return {
        "Content-Type": "application/json",
        "X-API-Key": process.env.REACT_APP_CUPI_API_KEY,
        aws_cognito_id_token: parsed.IdToken,
        aws_cognito_access_token: parsed.AccessToken,
      };
    } catch {
      return null;
    }
  }

  /* ----------------------------------------------------------------
   * Fetch every record once (and again on Reload / after the modal
   * updates a record). Pagination and filtering happen in the grid.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    let ignore = false;

    const fetchPage = async (headers, page) => {
      const { data: res } = await axios.get(`${API_BASE}/bank-account/all`, {
        headers,
        params: { flat: 1, page, limit: FETCH_LIMIT },
        timeout: 15000,
      });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    };

    (async () => {
      setFetching(true);
      try {
        const headers = getAuthHeaders();
        if (!headers) {
          // optional: redirect to login
          if (!ignore) setData([]);
          return;
        }

        const first = await fetchPage(headers, 1);
        // Use the size the API actually returned, in case it caps `limit`
        const perPage = first.items.length || FETCH_LIMIT;
        const totalPages = Math.ceil(first.total / perPage);

        const all = [...first.items];
        for (let p = 2; p <= totalPages; p += FETCH_CONCURRENCY) {
          const batch = [];
          for (let q = p; q < p + FETCH_CONCURRENCY && q <= totalPages; q++) {
            batch.push(fetchPage(headers, q));
          }
          const results = await Promise.all(batch);
          if (ignore) return;
          results.forEach((r) => all.push(...r.items));
        }

        // Guard against the same record appearing on two API pages
        const seen = new Set();
        const unique = all.filter((it) => {
          const key = it.reference_id;
          if (!key) return true;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        if (!ignore) setData(unique.map(mapItemToRow));
      } catch (e) {
        console.error("bank-account/all fetch failed:", e?.message || e);
        if (!ignore) setData([]);
      } finally {
        if (!ignore) setFetching(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [API_BASE, refresh]);

  /* ----------------------------------------------------------------
   * Quick search (debounced) — always restart from page 1
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const timeout = setTimeout(() => {
      apiRef.current?.setQuickFilterValues(parseQuickFilter(searchText.trim()));
      goToFirstPage();
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchText, apiRef]);

  /* ----------------------------------------------------------------
   * Date range filter — applied before the rows reach the grid, so the
   * table, the count, the companies and the status options all only
   * reflect records inside the range.
   * ---------------------------------------------------------------- */
  const gridRows = useMemo(() => {
    if (!isDateFilterActive) return data;
    const from = startDate ? startOfDay(startDate) : null;
    const to = endDate ? endOfDay(endDate) : null; // end date inclusive
    return data.filter((row) => isWithinRange(row.created_at, from, to));
  }, [data, startDate, endDate, isDateFilterActive]);

  const statusOptions = useMemo(
    () => [...new Set(gridRows.map((r) => r.__status))].sort(),
    [gridRows],
  );
  const statusKey = statusOptions.join("|");

  const columns = useMemo(
    () => [
      {
        field: "__date",
        headerName: "DATE",
        flex: 0.9,
        minWidth: 100,
        headerAlign: "center",
        align: "center",
        filterOperators: CONTAINS_ONLY,
        // sort by the real timestamp, not the formatted string
        sortComparator: (_v1, _v2, p1, p2) => {
          const a = new Date(p1.api.getRow(p1.id)?.created_at || 0).getTime();
          const b = new Date(p2.api.getRow(p2.id)?.created_at || 0).getTime();
          return a - b;
        },
      },
      {
        field: "__compName",
        headerName: "COMPANY NAME",
        flex: 1.4,
        minWidth: 160,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__accNo",
        headerName: "ACCOUNT NO",
        flex: 1.2,
        minWidth: 140,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__beneName",
        headerName: "BENEFICIARY NAME",
        flex: 1.3,
        minWidth: 150,
        filterOperators: CONTAINS_ONLY,
        renderCell: (params) => ellipsisCell(params.value),
      },
      {
        field: "__status",
        headerName: "STATUS",
        flex: 1,
        minWidth: 120,
        headerAlign: "center",
        align: "center",
        type: "singleSelect",
        valueOptions: statusKey ? statusKey.split("|") : [],
        filterOperators: IS_ONLY,
        renderCell: (params) => (
          <div className={styles.data1}>{params.value}</div>
        ),
      },
      {
        field: "__action",
        headerName: "ACTION",
        flex: 1.1,
        minWidth: 120,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <button
            className={styles.button}
            onClick={() => {
              setSelectedRow(params.row);
              setAccDetailsTab(true);
            }}
          >
            <img alt="" src="/eye.svg" className={styles.btn_img} />
            View More
          </button>
        ),
      },
    ],
    [statusKey],
  );

  const allColumnsHidden = useMemo(
    () => columns.every((col) => columnVisibilityModel[col.field] === false),
    [columns, columnVisibilityModel],
  );

  /* ----------------------------------------------------------------
   * Keep track of how many rows the current page really shows, so the
   * grid height fits them (no inner scrollbar, no empty gap).
   * ---------------------------------------------------------------- */
  const syncVisibleRowCount = () => {
    if (!apiRef.current) return;
    const count = gridPaginatedVisibleSortedGridRowIdsSelector(apiRef).length;
    setVisibleRowCount((prev) => (prev === count ? prev : count));
  };

  const rowsOnPage =
    Math.min(visibleRowCount, paginationModel.pageSize) || GRID_MIN_ROWS;
  const gridHeight = `min(${GRID_MAX_HEIGHT}, ${GRID_HEADER_HEIGHT + GRID_FOOTER_HEIGHT + rowsOnPage * GRID_ROW_HEIGHT
    }px)`;

  /* ----------------------------------------------------------------
   * Toolbar helpers
   * ---------------------------------------------------------------- */
  const handleSearchChange = (e) => setSearchText(e.target.value);

  const toggleSearch = () => {
    setSearchOpen((open) => {
      if (open) setSearchText("");
      return !open;
    });
  };

  const openFilterPanel = (event) => {
    setPanelAnchorEl(event.currentTarget);
    apiRef.current?.showFilterPanel();
  };

  const openColumnsPanel = (event) => {
    setPanelAnchorEl(columnsButtonRef.current ?? event?.currentTarget ?? null);
    apiRef.current?.showPreferences(GridPreferencePanelsValue.columns);
  };

  const handleFilterModelChange = (model) => {
    setFilterModel(model);
    goToFirstPage();
  };

  const handlePaginationChange = (model) => {
    setPaginationModel((prev) =>
      model.pageSize !== prev.pageSize
        ? { page: 0, pageSize: model.pageSize } // restart after size change
        : model,
    );
  };

  const resetGridFilters = () => {
    setSearchText("");
    setSearchOpen(false);
    setFilterModel(EMPTY_FILTER_MODEL);
    setColumnVisibilityModel(DEFAULT_HIDDEN_COLUMNS);
    setStartDate(null);
    setEndDate(null);
    setStartDateDraft(null);
    setEndDateDraft(null);
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  };

  /* ----------------------------------------------------------------
   * Date filter popover
   * ---------------------------------------------------------------- */
  const handleFilterClick = (event) => {
    setStartDateDraft(startDate);
    setEndDateDraft(endDate);
    setFilterAnchorEl(event.currentTarget);
    setFilterOpen(true);
  };

  const handleFilterClose = () => {
    setFilterOpen(false);
    setFilterAnchorEl(null);
  };

  const invalidDraftRange =
    startDateDraft && endDateDraft && startDateDraft > endDateDraft;

  const applyDateFilter = () => {
    setStartDate(startDateDraft);
    setEndDate(endDateDraft);
    goToFirstPage();
    handleFilterClose();
  };

  const clearDateFilter = () => {
    setStartDateDraft(null);
    setEndDateDraft(null);
    setStartDate(null);
    setEndDate(null);
    goToFirstPage();
    handleFilterClose();
  };

  const dateSummary = () => {
    if (startDateDraft && endDateDraft)
      return `Showing data from ${startDateDraft.toLocaleDateString()} to ${endDateDraft.toLocaleDateString()}`;
    if (startDateDraft)
      return `Showing data from ${startDateDraft.toLocaleDateString()}`;
    if (endDateDraft)
      return `Showing data up to ${endDateDraft.toLocaleDateString()}`;
    return "Please select a date range";
  };

  /* ----------------------------------------------------------------
   * Export — every row matching the current date range, search and
   * column filters (all pages), in the current sort order.
   * ---------------------------------------------------------------- */

    const handleExport = () => {
      const filtered = data; // all records, ignoring date range, search and column filters

      if (!filtered.length) {
        alert("No data to export");
        return;
      }

      const exportRows = filtered.map((row) => ({
        Date: row.__date,
        "Company Name": row.__compName,
        "Account No": row.__accNo,
        "Beneficiary Name": row.__beneName,
        Status: row.__status,
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Account Validation");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, `account_validation_${new Date().toISOString()}.xlsx`);
    };

    return (
      <>
        {fetching && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(255,255,255,0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 2100,
            }}
          >
            <CircularProgress size={60} sx={{ color: "#2348AB" }} />
          </div>
        )}

        <div className={styles.frameGroup}>
          <div className={styles.feeStatementDataTable}>
            {/* ── top bar ─────────────────────────────────────────── */}
            <div className={styles.top}>
              <div className={styles.filterWrapper}>
                <div className={styles.filter}>
                  <div className={styles.containertext}>
                    {title || "Account Validation Request"}
                  </div>
                </div>

                <div className={styles.filter}>
                  {searchOpen && (
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search company, account, beneficiary…"
                      value={searchText}
                      onChange={handleSearchChange}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") toggleSearch();
                      }}
                      sx={{
                        minWidth: 240,
                        mr: 1,
                        "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                        "& .MuiInputBase-input": {
                          fontFamily: SARABUN,
                          fontSize: 14,
                          color: TEXT_COLOR,
                        },
                        "& .MuiInputBase-input::placeholder": {
                          fontFamily: SARABUN,
                          color: MUTED_COLOR,
                          opacity: 1,
                        },
                      }}
                    />
                  )}

                  <button
                    className={styles.reLoadTable}
                    onClick={toggleSearch}
                    title="Search"
                  >
                    <img
                      className={styles.vectorIcon1}
                      alt="Search"
                      src="/SearchSVG.svg"
                    />
                  </button>

                  <button
                    ref={columnsButtonRef}
                    className={styles.reLoadTable}
                    onClick={openColumnsPanel}
                    title="Show / hide columns"
                  >
                    <img
                      className={styles.vectorIcon1}
                      alt="Columns"
                      src="/ThreeHorizontalLine.svg"
                    />
                  </button>

                  <button
                    className={styles.reLoadTable}
                    onClick={openFilterPanel}
                    title="Column filters"
                  >
                    <img
                      className={styles.vectorIcon1}
                      alt="Filter"
                      src="/ThreeVerticalline.svg"
                    />
                  </button>

                  <button
                    className={styles.reLoadTable}
                    onClick={handleFilterClick}
                    title="Date filter"
                    style={
                      isDateFilterActive
                        ? { borderColor: ACCENT_COLOR, color: ACCENT_COLOR }
                        : undefined
                    }
                  >
                    <img
                      className={styles.vectorIcon1}
                      alt=""
                      src="/DateIcon.svg"
                    />
                  </button>

                  <button
                    className={styles.reLoadTable}
                    onClick={handleExport}
                    title="Export"
                  >
                    <img
                      className={styles.vectorIcon1}
                      alt=""
                      src="/NewDownloadIcon.svg"
                    />
                  </button>

                  <button
                    className={styles.reLoadTable}
                    onClick={() => {
                      resetGridFilters();
                      setRefresh((r) => !r);
                    }}
                    title="Reload"
                  >
                    <img
                      className={styles.vectorIcon12}
                      alt="Reload"
                      src="/vector7.svg"
                    />
                  </button>
                </div>
              </div>
              <div className={styles.topChild} />
            </div>

            {/* ── DataGrid ───────────────────────────────────────── */}
            {!fetching && (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  minWidth: 0,
                  height: gridHeight,
                  ...(allColumnsHidden && { minHeight: 280 }),
                  overflow: "hidden",
                  fontFamily: SARABUN,
                  "& .MuiDataGrid-cell": {
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "none",
                    overflow: "hidden",
                  },
                  "& .MuiDataGrid-footerContainer": { border: "none" },
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                }}
              >
                <DataGrid
                  apiRef={apiRef}
                  rows={gridRows}
                  rowHeight={GRID_ROW_HEIGHT}
                  columnHeaderHeight={GRID_HEADER_HEIGHT}
                  columns={columns}
                  disableColumnMenu
                  disableRowSelectionOnClick
                  filterModel={filterModel}
                  onFilterModelChange={handleFilterModelChange}
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityModelChange={setColumnVisibilityModel}
                  pagination
                  paginationMode="client"
                  filterMode="client"
                  sortingMode="client"
                  paginationModel={paginationModel}
                  onPaginationModelChange={handlePaginationChange}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  initialState={{
                    sorting: { sortModel: [{ field: "__date", sort: "desc" }] },
                  }}
                  onStateChange={syncVisibleRowCount}
                  slotProps={{
                    baseSelect: {
                      MenuProps: { PaperProps: { sx: SELECT_MENU_SX } },
                    },
                    panel: {
                      anchorEl: panelAnchorEl,
                      placement: "bottom-end",
                      sx: PANEL_SX,
                    },
                    filterPanel: {
                      filterFormProps: {
                        logicOperatorInputProps: { sx: { display: "none" } },
                        operatorInputProps: { sx: { display: "none" } },
                        columnInputProps: { sx: { minWidth: 160 } },
                        valueInputProps: {
                          InputComponentProps: { sx: { minWidth: 200 } },
                        },
                      },
                      sx: {
                        "& .MuiDataGrid-filterFormLogicOperatorInput": {
                          display: "none",
                        },
                        "& .MuiDataGrid-panelWrapper": { minWidth: 460 },
                        "& .MuiDataGrid-panelContent": { overflowX: "auto" },
                        "& .MuiDataGrid-filterForm": {
                          p: 2,
                          gap: 1,
                          alignItems: "flex-end",
                          flexWrap: "wrap",
                        },
                        "& .MuiDataGrid-filterFormDeleteIcon": {
                          flexShrink: 0,
                          alignSelf: "flex-end",
                        },
                      },
                    },
                  }}
                  localeText={{
                    noRowsLabel: isDateFilterActive
                      ? "No records in the selected date range."
                      : "No records found.",
                    noResultsOverlayLabel:
                      "No records match this search or filter.",
                    filterPanelColumns: "Column",
                    filterPanelInputLabel: "Value",
                    filterPanelInputPlaceholder: "Filter value",
                  }}
                  sx={{
                    height: "100%",
                    width: "100%",
                    maxWidth: "100%",
                    border: "none",
                    fontFamily: SARABUN,
                    color: TEXT_COLOR,

                    "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-cell, & .MuiDataGrid-cell *, & .MuiDataGrid-overlay, & .MuiDataGrid-footerContainer, & .MuiTablePagination-root":
                    {
                      fontFamily: SARABUN,
                    },

                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#f7f8fa",
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: 600,
                      fontSize: 14,
                      letterSpacing: 0.4,
                      color: HEADER_COLOR,
                    },
                    "& .MuiDataGrid-columnSeparator": { display: "none" },
                    "& .MuiDataGrid-cell": {
                      display: "flex",
                      alignItems: "center",
                      borderBottom: "none",
                      overflow: "hidden",
                      fontSize: 14,
                    },
                    "& .MuiDataGrid-footerContainer": { borderTop: "none" },
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                    {
                      outline: "none",
                    },
                  }}
                />

                {allColumnsHidden && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 12,
                      backgroundColor: "#fff",
                      zIndex: 2,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        color: "#667085",
                        fontFamily: SARABUN,
                      }}
                    >
                      All columns are hidden.
                    </span>
                    <button className={styles.button} onClick={openColumnsPanel}>
                      Manage Columns
                    </button>
                  </div>
                )}
              </Box>
            )}
          </div>
        </div>

        {/* Date range filter popover */}
        <Popover
          open={filterOpen}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: { p: 2, width: 380, borderRadius: 2 },
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <DatePicker
                label="Start Date"
                value={startDateDraft}
                onChange={(newValue) => setStartDateDraft(newValue)}
                maxDate={endDateDraft || undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="End Date"
                value={endDateDraft}
                onChange={(newValue) => setEndDateDraft(newValue)}
                minDate={startDateDraft || undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
          </LocalizationProvider>

          <Typography
            align="center"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: invalidDraftRange ? "error.main" : "inherit",
            }}
          >
            {invalidDraftRange
              ? "Start date must be on or before end date"
              : dateSummary()}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            {/* <Button onClick={clearDateFilter} disabled={!isDateFilterActive && !startDateDraft && !endDateDraft}>
            Clear
          </Button> */}
            <Button onClick={clearDateFilter}>Cancel</Button>
            <Button
              variant="contained"
              onClick={applyDateFilter}
              disabled={(!startDateDraft && !endDateDraft) || invalidDraftRange}
              sx={{ backgroundColor: "#2348AB" }}
            >
              Apply
            </Button>
          </Box>
        </Popover>

        {/* Modal */}
        {accDetailsTab && (
          <div className={styles.ViewUserHeader}>
            <AccValidationModal
              isVisible={accDetailsTab}
              row={selectedRow}
              setRefreshData={setRefresh}
              onClose={() => {
                setAccDetailsTab(false);
                setSelectedRow(null);
              }}
            />
          </div>
        )}
      </>
    );
  };

  export default AccValidation;